import React, { useState, useEffect, memo } from "react";
import { ChevronRight } from "lucide-react";
import agentProgress from "~/assets/icons/agent-progress.gif";
import { ShimmerText } from "~/components/assistant/ShimmerText";
import { defaultMessageTypeRenderers } from "~/components/assistant/lib/defaultRenderers";
import {
  getMessageTypeRenderer,
  registerMessageType,
} from "~/components/assistant/lib/messageTypeRegistry";
import { ErrorMessage } from "~/components/assistant/messages/ErrorMessage";
import { ToolMessage } from "~/components/assistant/messages/ToolMessage";
import type { Message as MessageType, MessagePart } from "~/components/assistant/types";
import { cn } from "~/lib/utils";
import { useAiThinkingParts } from "~/pages/datasets/components/assistant/hooks/useAiThinkingParts";

// Register default message type renderers
defaultMessageTypeRenderers.forEach((renderer) => registerMessageType(renderer));

// Collapsed tool calls component
function CollapsedToolCalls({
  toolCallParts,
  streaming,
}: {
  toolCallParts: MessagePart[];
  streaming?: boolean;
}) {
  // Auto-expand when streaming, collapse when done
  const [isExpanded, setIsExpanded] = useState(streaming ?? false);
  const count = toolCallParts.length;

  useEffect(() => {
    setIsExpanded(!!streaming);
  }, [streaming]);

  return (
    <div className="px-2 pt-2">
      <details
        open={isExpanded}
        onToggle={(e) => {
          const target = e.target as HTMLDetailsElement;
          setIsExpanded(target.open);
          e.stopPropagation();
        }}
      >
        <summary className="list-none cursor-pointer select-none w-fit">
          <div className="flex items-center">
            <span className="text-xs font-medium text-purple-600 px-1">
              {count} tool {count === 1 ? "call" : "calls"}
            </span>
            <ChevronRight
              className={cn(
                "size-4 text-purple-600 transition-transform duration-200",
                isExpanded && "rotate-90"
              )}
            />
          </div>
        </summary>
        <div className="mt-3 space-y-2">
          {toolCallParts.map((part) =>
            part.toolCall ? (
              <ToolMessage
                key={`tool-${part.toolCall.tool_call_id}`}
                toolCall={part.toolCall}
                expanded={streaming ? part.toolCall.running ?? false : false}
              />
            ) : null
          )}
        </div>
      </details>
    </div>
  );
}

interface MessageContentProps {
  message: MessageType;
  debugMode: boolean;
}

export const MessageContent = memo(function MessageContent({
  message,
  debugMode,
}: MessageContentProps) {
  const showThinkingParts = useAiThinkingParts();

  // Get message parts
  const parts = message.parts || [];

  const shouldShowProgress = !!message.streaming;

  const [isFadingProgressOut, setIsFadingProgressOut] = useState(false);
  const [showProgress, setShowProgress] = useState(shouldShowProgress);

  // Handle fade-out animation when streaming stops
  useEffect(() => {
    if (shouldShowProgress) {
      // Show progress immediately when streaming starts
      setShowProgress(true);
      setIsFadingProgressOut(false);
    } else if (showProgress) {
      // Start fade-out when streaming stops
      setIsFadingProgressOut(true);

      // Hide the element after fade-out animation completes
      const timer = setTimeout(() => {
        setShowProgress(false);
        setIsFadingProgressOut(false);
      }, 250); // 250ms matches the transition duration

      return () => clearTimeout(timer);
    }
  }, [shouldShowProgress, showProgress]);

  if (message.kind === "user_chat") {
    return <p className="text-[14px] leading-[22px] text-foreground">{message.content}</p>;
  }

  // Handle warning messages
  if (message.warning) {
    return <p className="text-sm text-gray-600 italic pb-4 pl-3 pt-2">{message.warning}</p>;
  }

  // Handle error messages
  if (message.error) {
    return <ErrorMessage errorText={message.error} traceId={message.traceId} />;
  }

  // Helper function to render a single message part
  const renderMessagePart = (part: MessagePart, index: number) => {
    // Get renderer from registry
    const renderer = getMessageTypeRenderer(part.type);

    if (!renderer) {
      return (
        <div key={`unknown-${index}`} className="p-2 bg-yellow-50 rounded">
          <p className="text-xs text-yellow-700">Unknown message type: {part.type}</p>
          {debugMode && <pre className="text-xs mt-1">{JSON.stringify(part, null, 2)}</pre>}
        </div>
      );
    }

    const Component = renderer.component;
    return <Component key={`${part.type}-${index}`} part={part} debugMode={debugMode} />;
  };

  // Helper function to render all parts
  const renderParts = () => {
    if (parts.length === 0) {
      return null;
    }

    // Sort parts by order
    const sortedParts = [...parts].sort((a, b) => a.order - b.order);
    const toolCallParts = sortedParts.filter((p) => p.type === "toolCall");
    const otherParts = sortedParts.filter((p) => p.type !== "toolCall" && p.type !== "thinking");

    return (
      <div className="space-y-2 pt-2">
        {toolCallParts.length > 0 && (
          <CollapsedToolCalls toolCallParts={toolCallParts} streaming={message.streaming} />
        )}
        {otherParts.map((part, index) => renderMessagePart(part, index))}
      </div>
    );
  };

  // Check if any tool call is currently running
  const hasRunningToolCall = parts.some((p) => p.type === "toolCall" && p.toolCall?.running);

  // Get the latest thinking part to show while a tool call is in progress
  const latestThinking =
    showThinkingParts && message.streaming && hasRunningToolCall
      ? [...parts]
          .sort((a, b) => a.order - b.order)
          .filter((p) => p.type === "thinking")
          .pop()
      : null;

  return (
    <div className="space-y-4">
      <div className="text-[14px] leading-[20px] text-foreground">
        {renderParts()}
        {showProgress && (
          <div
            className={cn("flex items-center transition-opacity duration-250 ml-3 pt-4 gap-2", {
              "opacity-0": isFadingProgressOut,
              "opacity-100": !isFadingProgressOut,
            })}
          >
            {!latestThinking?.content && (
              <img src={agentProgress} alt="Loading..." className="w-[18px]" draggable={false} />
            )}
            {latestThinking?.content && (
              <ShimmerText
                className="text-sm"
                style={
                  {
                    "--base-color": "var(--gray-600)",
                    "--shimmer-color": "var(--gray-800)",
                  } as React.CSSProperties
                }
              >
                {latestThinking.content}
              </ShimmerText>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
